import { GameState } from './GameState';
import { GameConfig } from '../core/GameConfig';
import { Utils } from '../core/Utils';
import { socialSystem } from './SocialSystem';
import { encounterSystem, EncounterOffer, ParkPlan } from './EncounterSystem';
import { LocationId } from '../data/tables/Locations';

/**
 * 自由出行系统（周末模型 2026-10-11，设计基准 docs/自由出行与小地图-设计方案.md §二/§三）
 *
 * 职责：周末阶段的三件事——①在家休息结算（+10 体力）；
 * ②影视城探班 / 拾光咖啡馆兼职两项「周末限定」的业务逻辑；
 * ③本周末出行的进行态（剩余行动点）与扣点前的内容判据 `availability`
 *   （2026-10-12 B 批从 FreeroamMap 的组件字段搬进来：业务账不许长在 UI 上）。
 * 地图交互/UI 与偶遇 VN 演出在 FreeroamMap 组件（公园偶遇走 EncounterSystem.rollPark）。
 *
 * 赴约选周自动化（2026-10-28 茄子令）：人脉页邀约成功（SocialSystem.invite，答应即挂 player.pendingInvites）
 * → 玩家在聊天页选定本月第几周 → 到选定周的周末由 MonthProgressPanel 自动演出赴约
 * （FreeroamSystem.resolvePendingInvite，+INVITE_WEEKEND_GAIN 好感此时才结算），无需进咖啡馆。
 * 爽约机制随自动化取消（到点必演，玩家没有毁约的入口）；`week` 未定的挂起=约未成形，收月作废。
 * 一次只挂一个约。
 */
/** 剧组里可探班的一位（roleIndex 供 UI 取角色名，系统层不许背剧本表） */
export interface FilmCrewMember {
    npcId: string;
    name: string;
    roleIndex: number;
}

/** 一个在拍剧组：scriptId + 组里已结识的成员（空数组=组在拍但没熟人可探） */
export interface FilmCrew {
    scriptId: string;
    known: FilmCrewMember[];
}

class FreeroamSystem {
    /** 周末在家休息：+10 体力（2026-10-11 拍板数值）。
     *  2026-10-12 茄子令撤二选一卡后这条路由地图屏那颗钮触发 ⇒ 出行进行态一并清掉。
     *  不落盘（2026-11-03 月存口径：写盘只在每月月初那一格，月内交换等它捎带）。 */
    weekendStayHome(): void {
        const state = GameState.getInstance();
        state.addEnergy(GameConfig.WEEKEND_REST_ENERGY);
        this.outing = null;
    }

    /** 周末出门收口：探班/兼职/购物的结算都发生在各自的交互内，这里只清出行进行态（内存账，无需落盘——
     *  刷新=这周末重过是既定口径；写盘只在月初那一格，判据见实现说明 §3.2b）。 */
    endOuting(): void {
        this.outing = null;
    }

    // ===== 出行进行态（2026-10-12 B 批从 FreeroamMap 的组件字段搬进来） =====

    /**
     * 本周末出行的进行态（内存态，不落盘——沿用出行周方案口径：刷新/读档=这周末重过）。
     * 搬进来的理由：`points` 决定还能不能选目的地，是业务账，
     * 长在 UI 组件上⇒跨屏路径一异常就丢，而且 UI 没法在扣点前回答「今天这个地方有内容吗」。
     * （原 `cafeVisited` 随爽约机制一起删——赴约选周自动化后「去没去过咖啡馆」不再是账。）
     */
    private outing: { points: number } | null = null;

    /** 周末选「出门」：开一次出行，行动点重置 */
    startOuting(): void {
        this.outing = { points: GameConfig.OUTING_ACTION_POINTS };
    }

    /** 剩余行动点（没出门=0）。10-12 撤卡后 UI 只剩一处读它：`FreeroamMap.endVisit` 判「演出读完是退回地图还是直接收口」；
     *  出口钮文案与 pin 都不再读点数（文案三档按屏写，目的地全开放不压灰，见设计方案 §三.4 与 §十一.11）。 */
    get pointsLeft(): number {
        return this.outing?.points ?? 0;
    }

    /**
     * 这个目的地今天有没有内容——纯查询，不许写状态、不许扣点（与 `EncounterSystem.roll` 只出计划、
     * 交付方盖章同形状）。判据集中在这一个口，UI 只负责在扣点前把 reason 说给玩家。
     */
    availability(loc: LocationId): { ready: boolean; reason?: string } {
        if (!this.outing) return { ready: false, reason: '还没出门' };
        if (this.outing.points <= 0) return { ready: false, reason: '本周末行动点已用完' };
        // 商业街本身不扣点（2026-10-13 茄子令：进街免费、进店才扣 1 点）⇒ 街景永远进得去，
        // 点数闸只拦「进店」（enterShop 自己走 spendPoint 前的 canSpend）与公园/影视城。
        if (loc === 'loc_street') return { ready: true };
        if (loc === 'loc_filmcity') {
            // 「今天有没有剧组在拍」不再是门口判据（2026-10-15 茄子令「没有可探班的剧组也可以进入，进去以后再提示」）：
            // 影视城是地点，门永远开；空态由屏内那句话交代（`filmcityEmptyText`）。
            // 连带改掉 2026-10-12 定①乙 那条理由（「闸若认熟人，灰行永远见不到＝空条款」）——
            //    现在连"有没有组"都不问了，那排灰行当然见得到；代价＝可能白花本周唯一的 1 点进去看提示，茄子知情。
            const crews = this.filmcityCrews();
            // 月闸启用后补的第二级（同批，2026-10-12）：有熟人、但组里认识的人本月全探过 ⇒ 门口先把话说完，
            // 别让人花掉本周唯一点进来才发现一排「本月已探望」。这条**保留**（10-15 令字面只管"没有剧组"那一档）。
            // ⚠️ 必须再套一层「压根没熟人」的判据再收：全组都是生人时 `anyLeft` 也是 false，
            //    照它判不成立就把灰行看得见那档判掉了——我第一版就踩了这一下。
            const knownAny = crews.some(crew => crew.known.length > 0);
            const anyLeft = crews.some(crew => crew.known.some(m => this.canVisit(m.npcId)));
            if (knownAny && !anyLeft) return { ready: false, reason: '本月已探班，下月可再来' };
        }
        return { ready: true };
    }

    /** 前往某地：花掉本周末唯一的 1 点（调用方须先过 `availability`） */
    spendPoint(): void {
        if (this.outing) this.outing.points -= 1;
    }

    /** 进店扣点前的查询（商业街免进街点后，1 点落在「进店」这一步；⛔ 无点可花=进不了店） */
    canSpend(): boolean {
        return !!this.outing && this.outing.points > 0;
    }

    /**
     * 咖啡馆兼职（2026-10-28 茄子令：进店可选「兼职」）：一句旁白 + 金钱 +CAFE_JOB_PAY；
     * 赴约已选周自动化（到点自动演出），「随便坐坐」与兼职是进店菜单的两格。
     */
    workAtCafe(): { lines: { text: string; changes?: { label: string; delta: string }[] }[] } {
        const state = GameState.getInstance();
        state.addMoney(GameConfig.CAFE_JOB_PAY);
        return {
            lines: [
                { text: '你在拾光咖啡馆帮了一下午工，擦桌子、端咖啡，收工时老板把工资结给了你。', changes: [{ label: '金钱', delta: `+¥${Utils.formatMoney(GameConfig.CAFE_JOB_PAY)}` }] },
            ],
        };
    }

    /**
     * 「随便坐坐」掷点（2026-10-28 茄子令）：CAFE_LOUNGE_MEET_PCT 掷中=偶遇（池与公园完全同款，
     * rollCafe）；没中=独坐档。掷点归系统层——业务账不许长在 UI 上。
     */
    cafeLoungeRoll(): { kind: 'sit' } | { kind: 'meet'; plan: ParkPlan } {
        if (Math.random() * 100 < GameConfig.CAFE_LOUNGE_MEET_PCT) return { kind: 'meet', plan: encounterSystem.rollCafe() };
        return { kind: 'sit' };
    }

    /** 独坐档：一句独坐文案 + 体力 +CAFE_SIT_ENERGY（掷中休息、或偶遇池抽空时的兜底） */
    cafeSit(): { lines: { text: string; changes?: { label: string; delta: string }[] }[] } {
        const state = GameState.getInstance();
        state.addEnergy(GameConfig.CAFE_SIT_ENERGY);
        return {
            lines: [
                { text: '你在拾光咖啡馆靠窗的位子坐了一会儿，看着街上人来人往，什么也没想。', changes: [{ label: '体力', delta: `+${GameConfig.CAFE_SIT_ENERGY}` }] },
            ],
        };
    }

    /** 公园散步休息档（掷中 PARK_MEET_PCT 未中、或池抽空的兜底）：一句散步文案 + 体力 +PARK_REST_ENERGY */
    parkRest(): { lines: { text: string; changes?: { label: string; delta: string }[] }[] } {
        const state = GameState.getInstance();
        state.addEnergy(GameConfig.PARK_REST_ENERGY);
        return {
            lines: [
                { text: '你在公园长椅上坐了一下午，晒晒太阳、看看鸽子，起身时整个人都松快了。', changes: [{ label: '体力', delta: `+${GameConfig.PARK_REST_ENERGY}` }] },
            ],
        };
    }

    /**
     * 赴约演出数据（选周自动化 2026-10-28，原 `meetAtCafe` 改名——不再是「进店赴约」，
     * 由 MonthProgressPanel 在选定周的周末直接调用）：好感 +INVITE_WEEKEND_GAIN 此时结算，**该条挂起当场从数组移除**。
     * 多槽批 2026-10-29：按传入的那条约结算（同周多个约由调用方按挂约顺序逐条传入）。
     * `npcId` 一并回：UI 要拿它去取捏脸配置出正脸（2026-10-12 修「赴约/探班只有个姓、没有脸」，
     * 原先只回一个名字 ⇒ 演出侧无从挂头像）。
     */
    resolvePendingInvite(invite: { npcId: string; week?: number }): { ok: true; npcId: string; npcName: string; lines: { text: string; changes?: { label: string; delta: string }[] }[] } | { ok: false } {
        const state = GameState.getInstance();
        const list = state.player.pendingInvites ?? [];
        const idx = list.indexOf(invite);
        // 按引用移除：项若已不在账上（重复结算/坏档）＝不成行，⛔ 再结一次好感
        if (idx < 0) return { ok: false };
        list.splice(idx, 1);
        const npcId = invite.npcId;
        const npcName = socialSystem.getNpcName(npcId);
        const gain = GameConfig.INVITE_WEEKEND_GAIN;
        state.addFavor(npcId, gain);
        const texts = socialSystem.getNpcTexts(npcId);
        // 不落盘（月存口径 2026-11-03）：好感与挂起清除等月初那一格捎带；月内退出重演=重看这场约会
        // 赴约见面对话：普通 NPC 从 inviteMeetTexts 池随机抽一条，特殊 NPC 读 inviteMeetText 单条
        const meetLine = texts?.inviteMeetTexts
            ? texts.inviteMeetTexts[Math.floor(Math.random() * texts.inviteMeetTexts.length)]
            : texts?.inviteMeetText ?? '「来了。」';
        return {
            ok: true,
            npcId,
            npcName,
            lines: [
                { text: meetLine, changes: [{ label: '好感', delta: `+${gain}` }] },
            ],
        };
    }

    // ===== 环亚影视城（探班 · 先选剧组、再选组里见谁，§四.6） =====

    /**
     * 在拍剧组名单：一个剧组 = 一部有普通 NPC 处于拍摄期的剧本（按 NPC 与 job 的原始顺序聚合，
     * 同存档稳定，不许排序规则——那是第二套真值）。
     * 2026-10-12 茄子令「探班的选项是选剧组不是选人」：名单从「人 → 剧」平表改成按剧聚合，
     * `known` 才是可探班的人（已结识 = favor ≥ 0），空 `known` 的组照常上屏（置灰可点，判据见 `availability`）。
     * 2026-10-15 茄子令「探班只能探自己没参演的剧组」：**玩家有合同的那部剧整组不进名单**
     * （组里本来就是同事，不用特地跑一趟）。尺与好友客串 `GuestCameoSystem.inviteCandidates` 同一把
     * （2026-10-03 令＝「该剧有合同即算参演」，不筛 active/airing/done），不许在 UI 侧另算一遍。
     * 特殊 NPC（竞争型）2026-10-15 起实例化进派角池、接戏后在 normalNpcs 里同样有 activeJobs ⇒ 正常进名单；
     * 资源型/导演类（无 rival）不进派角池、不接 job，结构上不会出现在这里。
     * 剧本名/角色名一律由 UI 层回 GameData.SCRIPT_MAP 取（系统层少背一张表）。
     */
    filmcityCrews(): FilmCrew[] {
        const state = GameState.getInstance();
        const ownScripts = new Set(state.player.contracts.map(c => c.scriptId));
        const byScript = new Map<string, FilmCrew>();
        for (const npc of state.player.normalNpcs) {
            const known = state.getFavor(npc.id) >= 0;
            for (const job of npc.activeJobs ?? []) {
                if (job.phase !== 'shooting') continue;
                if (ownScripts.has(job.scriptId)) continue;   // 自己参演的组＝同事，不进探班名单
                let crew = byScript.get(job.scriptId);
                if (!crew) { crew = { scriptId: job.scriptId, known: [] }; byScript.set(job.scriptId, crew); }
                // 一 NPC 同时在本批被允许挂两本（NormalNpcSystem 只挡同剧重签）⇒ 按 job 分组会把他列进两个组；
                // 旧写法 `.find(phase==='shooting')` 只取第一本，第二组里漏他。此处取的是全 job，差异已报备。
                if (known) crew.known.push({ npcId: npc.id, name: npc.name, roleIndex: job.roleIndex });
            }
        }
        return [...byScript.values()];
    }

    /**
     * 探班屏空态那句话（2026-10-15 茄子令：门口不许挡，进屏后才说）。调用方仅在 `filmcityCrews()` 为空时取。
     * 两种空必须分开讲：全镇没组在拍 vs 只有你自己的组在拍——后者若照抄前一句，玩家明明正在那个组里拍戏，
     * 「今天没有剧组在这里拍戏」当场成假话。
     */
    filmcityEmptyText(): string {
        const anyShooting = GameState.getInstance().player.normalNpcs
            .some(n => (n.activeJobs ?? []).some(j => j.phase === 'shooting'));
        return anyShooting
            ? '这里正在拍的是你自己的剧组，组里都是同事，不用特地探班。'
            : '今天没有剧组在这里拍戏，改天再来探班吧。';
    }

    /** 该 NPC 本月还能不能探班（月闸 2026-10-12 茄子令启用，`VISIT_MONTHLY_LIMIT`=1；旋钮若调回 0 仍是不设限形态）。
     *  UI 侧同一把尺读它决定子行压灰与「本月已探望」标记，不许另算一遍。 */
    canVisit(npcId: string): boolean {
        const limit = GameConfig.VISIT_MONTHLY_LIMIT;
        if (limit <= 0) return true;
        const state = GameState.getInstance();
        const abs = state.player.year * 12 + state.player.month;
        return (state.player.visitMonth?.[npcId] ?? 0) !== abs;   // 记账=最近一次探班月；月粒度下「≠本月」即未探
    }

    /**
     * 探班开场（2026-10-15 探班重构）：盖 visitMonth 月闸（防止本月重探），返回演出数据。
     * ⛔ 在此加好感——好感结算移到 resolveVisit（玩家选了闲聊才加 VISIT_GAIN，选离开则 0）。
     * 月闸在开场就盖：即使选「离开」也算探过班，本月不能再探同一个人（与原逻辑一致）。
     */
    visit(npcId: string): { ok: true; offer: EncounterOffer } | { ok: false } {
        const state = GameState.getInstance();
        if (!this.canVisit(npcId)) return { ok: false };
        const npcName = socialSystem.getNpcName(npcId);
        state.player.visitMonth = { ...(state.player.visitMonth ?? {}), [npcId]: state.player.year * 12 + state.player.month };
        const offer = encounterSystem.buildVisitOffer(npcId);
        // 不落盘（月存口径 2026-11-03）：月闸戳等月初那一格捎带。代价=月内退出重进可重探同一人刷好感
        //（单机 save-scumming，2026-10-28 茄子拍板容忍；10-13 堵的那个口子随统一模型重开，要堵回来说一声）
        return { ok: true, offer };
    }
}

/** 导出单例 */
export const freeroamSystem = new FreeroamSystem();
